import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { icons } from 'smartsuite-ui';
import {
    getGridStyles,
    getResponsiveLayoutMode,
    hasFilterWarning,
} from './dashboardUtils';
import TextOrLinkTitle from '../shared/TextOrLinkTitle';
import DashboardContext from '../shared/hooks/dashboardContext';
import { getNavigationLink } from './dashboardLinkUtils';
import { useLabelContext } from '../shared/hooks/labelContext';
import { buildWidgetFilterWarning } from '../shared/filtering/filtersModalUtils';
import { responsiveLayouts } from '../shared/constants/constants';

// eslint-disable-next-line
export default function GridPanel({
    availableFilters,
    children,
    dashboardFilterValues,
    dashboardWidth,
    openLinksInNewTab,
    panel,
    panelWidgetsFilterState,
    selectedResponsiveLayout,
    showPlaceholder,
}) {
    const { dashboardLabels, predicateLabels } = useLabelContext();
    const { uriConstants } = useContext(DashboardContext);
    const [uri, setUri] = useState(null);

    useEffect(() => {
        let isUnmounted = false;

        async function getUri() {
            const navigationUri = await getNavigationLink(
                panel.navigationLink,
                uriConstants,
                panelWidgetsFilterState,
                predicateLabels
            );
            if (!isUnmounted) {
                setUri(navigationUri);
            }
        }

        getUri();

        return () => {
            isUnmounted = true;
        };
    }, [
        panel.navigationLink,
        uriConstants,
        panelWidgetsFilterState,
        predicateLabels,
    ]);

    if (showPlaceholder) {
        return (
            <div
                className="grid-panel shimmer-container"
                style={{
                    ...getGridStyles(panel.position),
                    animationName: `shimmerColumns${panel.position.columnSpan}`,
                }}
            >
                <div className="shimmer-effect" />
            </div>
        );
    }

    // eslint-disable-next-line complexity
    function getPanelClassName(layoutSettings = {}, position = {}) {
        const layoutSettingsValue = layoutSettings ?? {};
        let panelClassName = 'grid-panel';

        if (position.isFullHeight) {
            panelClassName += ' grid-panel--full-height';
        }

        const layoutMode = getResponsiveLayoutMode(
            selectedResponsiveLayout,
            dashboardWidth
        );

        switch (layoutMode) {
            case responsiveLayouts.mobile: {
                panelClassName += ' grid-panel--mobile';
                return panelClassName;
            }
            case responsiveLayouts.tablet: {
                panelClassName += ' grid-panel--tablet';

                if (layoutSettingsValue.tablet?.isFullWidth) {
                    panelClassName += ' grid-panel--full-width';
                }

                if (layoutSettingsValue.tablet?.isCentered) {
                    panelClassName += ' grid-panel--centered';
                }
                return panelClassName;
            }
            default: {
                panelClassName += ' grid-panel--desktop';
                return panelClassName;
            }
        }
    }

    return (
        <div
            className={getPanelClassName(panel.layoutSettings, panel.position)}
            style={{
                ...getGridStyles(panel.position),
                ...panel.containerStyles,
            }}
        >
            {panel.header && (
                <div
                    className="title title--size-medium grid-panel__header-text"
                    style={panel.headerStyles}
                >
                    <TextOrLinkTitle
                        navigationLink={uri}
                        openLinkInNewTab={openLinksInNewTab}
                        title={panel.header}
                    />
                    {hasFilterWarning(
                        dashboardFilterValues,
                        panel.inapplicableFilterKeys
                    ) && (
                        <div className="filter-warning-icon filter-warning-icon--header">
                            <span
                                className={icons.warningFilled}
                                title={buildWidgetFilterWarning(
                                    panel.inapplicableFilterKeys,
                                    availableFilters,
                                    dashboardLabels
                                )}
                            />
                        </div>
                    )}
                </div>
            )}
            {children}
        </div>
    );
}

GridPanel.propTypes = {
    availableFilters: PropTypes.instanceOf(Object),
    children: PropTypes.node,
    dashboardFilterValues: PropTypes.instanceOf(Object),
    dashboardWidth: PropTypes.number.isRequired,
    openLinksInNewTab: PropTypes.bool.isRequired,
    panel: PropTypes.instanceOf(Object).isRequired,
    panelWidgetsFilterState: PropTypes.instanceOf(Array),
    selectedResponsiveLayout: PropTypes.string.isRequired,
    showPlaceholder: PropTypes.bool.isRequired,
};

GridPanel.defaultProps = {
    children: null,
    panelWidgetsFilterState: [],
};
